// ── Refresh keeps your place ────────────────────────────────────────────────
// Persists the user's current location (top-level view + the sub-position
// inside it) so a browser refresh reopens the same spot instead of always
// landing on HR → My Profile. AppWithJobSync writes the snapshot on every
// navigation (plus live tab/sub-view reports) and reads it once on boot.
//
// Two stores, on purpose. sessionStorage is PER TAB and survives reload, so
// it is the authority: with MOS open in two tabs, refreshing one must reopen
// THAT tab's screen, not wherever the other tab wandered to. localStorage is
// the shared copy. Both are cleared on sign-out.
//
// A brand-new tab opens on your own dashboard (HR → My Profile), the MOS home
// page (Andrea, 2026-10-17: "Make your dashboard as the homepage in MOS"). The
// one exception is a shop screen: a plant tablet whose browser restarts must
// come back to the Plant board or the job's TABS it was parked on, never to a
// profile page, so a fresh tab still reads the shared copy when it names one
// (freshTabKeepsPlace). The inventory kiosk and the Plaid return write this
// tab's own copy, so they are not fresh tabs at all.

import type { ViewType } from '../types/views';
import { VIEW_PERMISSION } from './permissionCatalog';

/** Exported so main.tsx's clean-first-run reset can preserve it — an update
    reload changes the version, and that reset is what runs on the way in. */
export const LAST_LOCATION_KEY = 'mos-last-location';
const STORAGE_KEY = LAST_LOCATION_KEY;

/** Panes/popouts inside the job view that can round-trip a refresh. */
const JOB_TABS = new Set(['details', 'tabs', 'workflow', 'bidders', 'submittals', 'estimator', 'planner', 'files']);

export interface LastLocation {
  view: ViewType;
  /** Active tab inside a board (Sales / Estimating / Drafting / Plant). */
  workbenchTab?: string | null;
  /** Job open in job-details. */
  jobId?: string | null;
  /** Active pane (or popout) inside the job view. */
  jobTab?: string | null;
  /** HR sub-view ('my-dashboard', 'pto', 'employee-<id>', …). */
  hrSubView?: string | null;
  /** Board the open job was entered from, so Back still works after refresh. */
  navOrigin?: { view: ViewType; tab: string | null } | null;
  /** Card pop-up the job was jumped out of, so Back reopens it (PR #994). */
  salesPopoutJobId?: string | null;
  estimatingPopoutJobId?: string | null;
  /** True when the open estimator was deep-linked from an Estimating board
      card (its close navigates back to the card) vs opened manually on the
      job (its close stays in place). */
  estimatorDeepLink?: boolean;
  /** PM card pop-out a cross-board jump left open, so Back reopens it. */
  pmOpenCardId?: string | null;
}

// VIEW_PERMISSION is the exhaustive Record<ViewType, …> — every view carries a
// permission or the build fails — so key membership is the canonical "is this a
// real view" check for untrusted stored strings.
const isViewType = (v: unknown): v is ViewType => typeof v === 'string' && v in VIEW_PERMISSION;

/** This tab's own snapshot wins; a fresh tab falls back to the shared one. */
function readRaw(): { raw: string; own: boolean } | null {
  try {
    const own = sessionStorage.getItem(STORAGE_KEY);
    if (own) return { raw: own, own: true };
  } catch { /* storage disabled — fall through */ }
  try {
    const shared = localStorage.getItem(STORAGE_KEY);
    return shared ? { raw: shared, own: false } : null;
  } catch {
    return null;
  }
}

/**
 * Whether a brand-new tab reopens the shared place instead of the dashboard:
 * only a shop screen does (the Plant board, or a job's TABS, where every
 * department's floor screen lives).
 */
export function freshTabKeepsPlace(loc: Pick<LastLocation, 'view' | 'jobTab'>): boolean {
  return loc.view === 'plant-workbench' || (loc.view === 'job-details' && loc.jobTab === 'tabs');
}

export function loadLastLocation(): LastLocation | null {
  const loc = parseLastLocation();
  if (!loc) return null;
  return loc.own || freshTabKeepsPlace(loc.place) ? loc.place : null;
}

function parseLastLocation(): { place: LastLocation; own: boolean } | null {
  try {
    const read = readRaw();
    if (!read) return null;
    const raw = read.raw;
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object' || !isViewType(p.view)) return null;
    const place: LastLocation = {
      view: p.view,
      workbenchTab: typeof p.workbenchTab === 'string' ? p.workbenchTab : null,
      jobId: typeof p.jobId === 'string' ? p.jobId : null,
      jobTab: typeof p.jobTab === 'string' && JOB_TABS.has(p.jobTab) ? p.jobTab : null,
      hrSubView: typeof p.hrSubView === 'string' ? p.hrSubView : null,
      navOrigin:
        p.navOrigin && typeof p.navOrigin === 'object' && isViewType(p.navOrigin.view)
          ? { view: p.navOrigin.view, tab: typeof p.navOrigin.tab === 'string' ? p.navOrigin.tab : null }
          : null,
      salesPopoutJobId: typeof p.salesPopoutJobId === 'string' ? p.salesPopoutJobId : null,
      estimatingPopoutJobId: typeof p.estimatingPopoutJobId === 'string' ? p.estimatingPopoutJobId : null,
      estimatorDeepLink: p.estimatorDeepLink === true,
      pmOpenCardId: typeof p.pmOpenCardId === 'string' ? p.pmOpenCardId : null,
    };
    return { place, own: read.own };
  } catch {
    return null;
  }
}

export function saveLastLocation(loc: LastLocation): void {
  const json = JSON.stringify(loc);
  try { sessionStorage.setItem(STORAGE_KEY, json); } catch { /* best-effort */ }
  try { localStorage.setItem(STORAGE_KEY, json); } catch { /* best-effort */ }
}

/** Sign-out clears the snapshot so the next user on this browser doesn't
    inherit the previous session's location (or its job/employee ids). */
export function clearLastLocation(): void {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* best-effort */ }
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* best-effort */ }
}
